import {Grid} from '@material-ui/core';
import {useQuoteField} from 'Quoting/components/hooks/useHandleQuoteFieldChange';
import {CheckboxInputGrid, MappingState} from 'Quoting/components/shared/CheckboxInputGrid';
import {FormRowGrid, GridRowProps} from 'Quoting/components/shared/GridRow';
import {QuoteFormControlElement} from 'Quoting/components/shared/QuoteFormControlElement';
import {QuoteFormElement} from 'Quoting/components/shared/QuoteFormElement';
import {FormWrapper} from 'Quoting/components/shared/QuoteFormWrapper';
import {SectionLabel} from 'Quoting/components/shared/SectionLabel';
import {A360ProductCd} from 'Quoting/models/Quoting';
import {Map as ImmutableMap} from 'immutable';
import React from 'react';
import {useSelector} from 'react-redux';
import {ReducerState} from 'rootReducer';
import {getBenefitLabelMappingforGAI7} from '../../PlanTemplates/ACC/GAI7Utilities';
import {getPlanPropertiesPath} from '../../PlansPropertyPaths';
import {getExclusionsAndLimitationsPropertyPaths, getParentPath} from './PropertyPaths';

interface ExclusionsAndLimitationsProps {
  exclusionPaths: MappingState[];
}

export const ExclusionsAndLimitationsBlock = () => {
  const {selectedProduct, selectedPlan} = useSelector((state: ReducerState) => state.quoteForm);
  const parentPath = `benefitPackagingDetail.plans[${selectedPlan?.planIndex ?? 0}]`;

  const {getQuoteFieldValue, getBenefitLabelMapping} = useQuoteField();
  const planProperties = getPlanPropertiesPath(selectedPlan?.planIndex ?? 0);
  const situsStateCd = getQuoteFieldValue<string>('riskStateCd');
  const benefitPaths = getBenefitLabelMapping(
    `${getQuoteFieldValue<string>(planProperties.planCd)}exclusions`,
    parentPath,
    {
      type: 'exclusions',
      flag: 'isExclusion',
    }
  );

  const benefitPathsACC = getBenefitLabelMappingforGAI7(
    `${getQuoteFieldValue<string>(planProperties.planCd)}exclusions`,
    parentPath,
    {
      type: 'exclusions',
      flag: 'isExclusion',
    },
    situsStateCd
  );

  return (
    <FormWrapper title="Exclusions And Limitations" collapsible withSeparator>
      <Grid container alignItems="center" style={{padding: '0 2rem'}}>
        {getQuoteFieldValue<string>(planProperties.planCd) === 'GAI7'
          ? renderExclusionsAndLimitations(selectedProduct?.code, benefitPathsACC)
          : renderExclusionsAndLimitations(
              selectedProduct?.code,
              benefitPaths,
              getQuoteFieldValue<string>(planProperties.planCd) === 'GVA6'
            )}
      </Grid>
    </FormWrapper>
  );
};

const renderExclusionsAndLimitations = (
  selectedProductCd: A360ProductCd,
  benefitPaths: MappingState[],
  planCd?: boolean
) => {
  switch (selectedProductCd) {
    case A360ProductCd.HI:
      //@ts-ignore
      return <HIExclusionsAndLimitations exclusionPaths={benefitPaths} />;
    case A360ProductCd.CI:
      //@ts-ignore
      return <CIExclusionsAndLimitations exclusionPaths={benefitPaths} />;
    case A360ProductCd.ACC:
      //@ts-ignore
      return <ACCExclusionsAndLimitations exclusionPaths={benefitPaths} planCd={planCd} />;
    default:
      return <></>;
  }
};

const ACCExclusionsAndLimitations = ({exclusionPaths, planCd}: {exclusionPaths: MappingState[]; planCd: boolean}) => {
  const {selectedPlan} = useSelector((state: ReducerState) => state.quoteForm);
  const planProperties = getPlanPropertiesPath(selectedPlan?.planIndex ?? 0);
  const gridLength = 3;
  const {getQuoteFieldValue} = useQuoteField();

  return (
    <React.Fragment>
      <SectionLabel variant="label-only" header={'Exclusions'} />
      {Array.isArray(exclusionPaths) &&
      exclusionPaths.length > 0 &&
      getQuoteFieldValue<string>(planProperties.planCd) === 'GAI7' ? (
        <Grid container>
          {exclusionPaths.map(
            ({label, path}) =>
              label !== 'On the job accident' && (
                <Grid item key={label} xs={12} sm={6} md={gridLength} style={{padding: '1rem'}}>
                  <QuoteFormControlElement
                    controlType="checkbox"
                    id={path}
                    label={label}
                    checked={getQuoteFieldValue<boolean>(path)}
                    disabled
                  />
                </Grid>
              )
          )}
        </Grid>
      ) : (
        <CheckboxInputGrid
          state={exclusionPaths}
          gridLength={3}
          isMIState={planCd && getQuoteFieldValue<string>('riskStateCd') === 'MI'}
        />
      )}
    </React.Fragment>
  );
};

const HIExclusionsAndLimitations: React.FC<ExclusionsAndLimitationsProps> = ({exclusionPaths}) => {
  const {selectedPlan} = useSelector((state: ReducerState) => state.quoteForm);
  const {getQuoteFieldValue, updateQuoteFieldValue} = useQuoteField();
  const propertyPaths = getExclusionsAndLimitationsPropertyPaths();

  const planPropertiesPaths = getPlanPropertiesPath(selectedPlan?.planIndex);

  const selectedPlanCd = getQuoteFieldValue(planPropertiesPaths.planCd);

  const rows: GridRowProps[] = [
    {
      rowProps: {key: 'exclusions-and-limitaitions-row-1'},
      items: [
        {
          key: 'pre-ex-limitation-pregnancy-waiting-period',
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              dropdownVariant="AttributeValue"
              searchKey={`HIPreExLimitationAndPregnancyWaitingPeriod.preExLimitationAndPregnancyWaitingPeriodCd`}
              label={
                selectedPlanCd === 'GIM2'
                  ? 'Pre-Ex Limitation & Pregnancy Waiting Period'
                  : 'Pre-Ex Condition Limitation'
              }
              id={propertyPaths.preExLimitationAndPregnancyWaitingPeriodDetails.preExLimitationAndPregnancyWaitingPeriodCd}
              value={getQuoteFieldValue(
                propertyPaths.preExLimitationAndPregnancyWaitingPeriodDetails.preExLimitationAndPregnancyWaitingPeriodCd
              )}
              onChange={e =>
                updateQuoteFieldValue(
                  propertyPaths.preExLimitationAndPregnancyWaitingPeriodDetails
                    .preExLimitationAndPregnancyWaitingPeriodCd,
                  e.target.value as string
                )
              }
              disabled
              required
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
    {
      rowProps: {key: 'exclusions-and-limitaitions-row-2'},
      items: [
        {
          key: 'pre-existing-condition-limitation-period',
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              dropdownVariant="AttributeValue"
              searchKey={`HIPreExLimitationAndPregnancyWaitingPeriod.preExistingConditionLimitationPeriodCd`}
              label="Pre-Existing Condition Limitation Period (Limitation/Lookback)"
              id={propertyPaths.preExLimitationAndPregnancyWaitingPeriodDetails.preExistingConditionLimitationPeriodCd}
              value={getQuoteFieldValue(
                propertyPaths.preExLimitationAndPregnancyWaitingPeriodDetails.preExistingConditionLimitationPeriodCd
              )}
              onChange={e =>
                updateQuoteFieldValue(
                  propertyPaths.preExLimitationAndPregnancyWaitingPeriodDetails.preExistingConditionLimitationPeriodCd,
                  e.target.value as string
                )
              }
              disabled
              required
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: 'pregnancy-waiting-period',
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              dropdownVariant="AttributeValue"
              searchKey="HIPreExLimitationAndPregnancyWaitingPeriod.pregnancyWaitingPeriodMonthsCd"
              label="Pregnancy Waiting Period (Months)"
              id={propertyPaths.preExLimitationAndPregnancyWaitingPeriodDetails.pregnancyWaitingPeriodMonthsCd}
              value={getQuoteFieldValue(
                propertyPaths.preExLimitationAndPregnancyWaitingPeriodDetails.pregnancyWaitingPeriodMonthsCd
              )}
              onChange={e =>
                updateQuoteFieldValue(
                  propertyPaths.preExLimitationAndPregnancyWaitingPeriodDetails.pregnancyWaitingPeriodMonthsCd,
                  e.target.value as string
                )
              }
              disabled
              required
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
  ];

  return (
    <React.Fragment>
      <SectionLabel
        variant="label-only"
        header={
          selectedPlanCd === 'GIM2'
            ? 'Pre-Ex Limitation & Pregnancy Waiting Period'
            : 'Pre-Existing Condition Limitations'
        }
      />
      <FormRowGrid rows={rows} />
      <>
        <SectionLabel variant="label-only" header="Exclusions" />
        <CheckboxInputGrid state={exclusionPaths} gridLength={3} />
      </>
    </React.Fragment>
  );
};

const CIExclusionsAndLimitations: React.FC<ExclusionsAndLimitationsProps> = ({exclusionPaths}) => {
  const {selectedPlan, selectedProduct} = useSelector((state: ReducerState) => state.quoteForm);
  const {getQuoteFieldValue, updateQuoteFieldValue} = useQuoteField();
  const productCd = selectedProduct?.code ?? '';
  const CIExclusionsPath = `${getParentPath(selectedPlan?.planIndex ?? 0, 0)}.preExLimitation`;

  const isGVCI4Plan =
    getQuoteFieldValue<ImmutableMap<string, any>>(`benefitPackagingDetail.plans[${selectedPlan?.planIndex ?? 0}]`)?.get(
      'planCd'
    ) == 'GVCI4';

  const rows: GridRowProps[] = [
    {
      rowProps: {key: 'exclusions-and-limitaitions-row-1'},
      items: [
        {
          key: 'preExLimitation.preExConditionLimationCd',
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              dropdownVariant="AttributeValue"
              searchKey={`${productCd}PreExLimitation.preExConditionLimitationCd`}
              label="Pre-Ex Condition Limitation"
              value={getQuoteFieldValue(`${CIExclusionsPath}.preExConditionLimitationCd`)}
              id={`${CIExclusionsPath}.preExConditionLimitationCd`}
              onChange={e => updateQuoteFieldValue(`${CIExclusionsPath}.preExConditionLimitationCd`, e.target.value)}
              disabled
              required
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: 'preExLimitation.preExConditionLimationPeriod',
          hidden: getQuoteFieldValue(`${CIExclusionsPath}.preExConditionLimitationCd`) !== 'PreEx',
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              dropdownVariant="AttributeValue"
              searchKey={`${productCd}PreExLimitation.preExConditionLimitationPeriodCd`}
              label="Pre-Ex Condition Limitation Period"
              value={getQuoteFieldValue(`${CIExclusionsPath}.preExConditionLimitationPeriodCd`)}
              id={`${CIExclusionsPath}.preExConditionLimitationPeriodCd`}
              onChange={e =>
                updateQuoteFieldValue(`${CIExclusionsPath}.preExConditionLimitationPeriodCd`, e.target.value)
              }
              required
              disabled
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
  ];

  return (
    <React.Fragment>
      <SectionLabel variant="label-only" header="PRE-EXISTING CONDITION LIMITATIONS" />
      <FormRowGrid rows={rows} />
      {isGVCI4Plan ? null : (
        <>
          <SectionLabel variant="label-only" header="Exclusions" />
          <CheckboxInputGrid state={exclusionPaths} gridLength={3} />
        </>
      )}
    </React.Fragment>
  );
};
